import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "notistack";
import MUIAutocomplete from "../GeneralComponents/MUIAutocomplete";
import { isCanceledResponse } from "../../bl_libs/invokeApi";
import { active_students_list_api } from "../../DAL/Students/Students";
import { attendance_roster_api } from "../../DAL/Attendances/Attendances";
import { useContentSetting } from "../../Hooks/ContentSetting";
import {
  add_student_report_api,
  update_student_report_api,
} from "../../DAL/StudentReports/StudentReports";
import { general_class_periods_for_member_api } from "../../DAL/ClassPeriods/ClassPeriods";
import { get_min_max_page_value } from "../../utils/constant";
import {
  formatHifzLessonRatingLabel,
  HIFZ_LESSON_RATING_OPTIONS,
  HIFZ_SESSION_TYPE_OPTIONS,
} from "../../utils/associateCapabilities";
import { formatStudentReportListRow } from "../../utils/studentReportRow";
import { apiErrorText, apiSuccessText } from "../../utils/i18n/snackbarMessage";
import { formatStudentListLabel, resolveLessonBook } from "../../utils/studentDisplay";

export default function AddOrUpdateReport({
  onCloseDrawer,
  setReportsList,
  formType,
  selectedObject,
  reportsList,
  classId = null,
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentsList, setStudentsList] = useState([]);
  const [classPeriodsList, setClassPeriodsList] = useState([]);
  const [classPeriodsLoading, setClassPeriodsLoading] = useState(false);
  const { userInfo } = useContentSetting();
  const { user_type } = userInfo;
  const lesson_book = resolveLessonBook(userInfo);

  const [state, setState] = useState({
    part_mumber: 0,
    page_number: 0,
    line_number: 0,
    report_description: "",
    verse_number: 0,
    lesson_book: user_type === "student" ? lesson_book : "quran",
    session_type: "lesson",
    lesson_rating: 3,
    class_period_id: "",
    status: true,
  });

  const get_students_list = async (signal = null) => {
    if (classId) {
      const result = await attendance_roster_api(classId, signal);
      if (isCanceledResponse(result)) return;
      if (result.code === 200) {
        const roster = result.students || [];
        setStudentsList(
          roster.map((s) => ({
            ...s,
            _id: s.member ?? s._id,
            lesson_book: resolveLessonBook(s, "quran"),
            name: s.name,
          })),
        );
      } else {
        enqueueSnackbar(apiErrorText(result, t), { variant: "error" });
      }
      return;
    }
    const result = await active_students_list_api(signal);
    if (isCanceledResponse(result)) return;
    if (result.code === 200) {
      setStudentsList(result.students_list);
    } else {
      enqueueSnackbar(apiErrorText(result, t), { variant: "error" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (classId && !state.class_period_id) {
      enqueueSnackbar(t("report.classPeriodRequired"), { variant: "error" });
      return;
    }
    setIsLoading(true);

    let formData = {
      action_by: userInfo.user_type,
      lesson_book: state.lesson_book,
      page_number: state.page_number,
      line_number: state.line_number,
      report_description: state.report_description,
    };
    if (user_type === "student") {
      formData.student = userInfo._id;
    } else {
      formData.student =
        selectedStudent.member ??
        selectedStudent.memberId ??
        selectedStudent._id;
    }
    if (state.lesson_book === "quran") {
      formData.part_mumber = state.part_mumber;
      formData.verse_number = state.verse_number;
    }
    if (classId) {
      formData.class_id = classId;
      formData.session_type = state.session_type || "lesson";
      formData.lesson_rating = Number(state.lesson_rating);
      formData.class_period_id = state.class_period_id;
    }

    const result =
      formType === "ADD"
        ? await add_student_report_api(formData)
        : await update_student_report_api(formData, state._id);
    if (result.code === 200) {
      let new_report = formatStudentReportListRow(result.student_report, t);
      new_report.student_name =
        user_type === "student" ? userInfo.name : selectedStudent.name;
      new_report.action_info_name = userInfo.name;
      new_report.student = {
        _id: user_type === "student" ? userInfo._id : selectedStudent._id,
        name: user_type === "student" ? userInfo.name : selectedStudent.name,
      };
      if (classId) {
        new_report.class_period_id =
          result.student_report?.class_period_id || state.class_period_id || "";
        new_report.lesson_rating_label = formatHifzLessonRatingLabel(
          new_report.lesson_rating,
          t,
        );
      }

      if (formType === "ADD") {
        setReportsList((reportsList) => [new_report, ...reportsList]);
      } else {
        const normalizedUpdated = formatStudentReportListRow(result.student_report, t);
        const row =
          result.student_report
            ? {
                ...normalizedUpdated,
                student_name:
                  user_type === "student" ? userInfo.name : selectedStudent?.name,
                action_info_name: userInfo.name,
                student: {
                  _id:
                    user_type === "student" ? userInfo._id : selectedStudent?._id,
                  name:
                    user_type === "student" ? userInfo.name : selectedStudent?.name,
                },
                class_period_id:
                  result.student_report?.class_period_id ||
                  state.class_period_id ||
                  "",
                lesson_rating_label: formatHifzLessonRatingLabel(
                  result.student_report.lesson_rating,
                  t,
                ),
              }
            : normalizedUpdated;
        setReportsList((list) =>
          list.map((obj) => (obj._id === state._id ? row : obj)),
        );
      }
      onCloseDrawer();
      enqueueSnackbar(apiSuccessText(result, t, "success.saved"), { variant: "success" });
    } else {
      enqueueSnackbar(apiErrorText(result, t), { variant: "error" });
      setIsLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setState((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const handleChangeStudent = (value) => {
    setSelectedStudent(value);
    setState((old) => ({
      ...old,
      lesson_book: resolveLessonBook(value, "quran"),
    }));
  };

  useEffect(() => {
    if (formType === "EDIT" && selectedObject?._id) {
      setState({
        ...selectedObject,
        lesson_book: resolveLessonBook(selectedObject, "quran"),
        session_type: selectedObject.session_type || "lesson",
        lesson_rating:
          selectedObject.lesson_rating != null && selectedObject.lesson_rating !== ""
            ? Number(selectedObject.lesson_rating)
            : 3,
        class_period_id: selectedObject.class_period_id || "",
      });
      setSelectedStudent(selectedObject.student);
      return;
    }
    if (formType === "ADD") {
      setState({
        part_mumber: 0,
        page_number: 0,
        line_number: 0,
        report_description: "",
        verse_number: 0,
        lesson_book: user_type === "student" ? lesson_book : "quran",
        session_type: "lesson",
        lesson_rating: 3,
        class_period_id: "",
        status: true,
      });
      setSelectedStudent(null);
    }
    // Depend on _id (not the object reference) so inline `selectedObject={{}}`
    // from parents does not wipe the student selection on every re-render.
  }, [formType, selectedObject?._id, user_type, lesson_book]);

  useEffect(() => {
    if (user_type === "student") return;
    const ac = new AbortController();
    get_students_list(ac.signal);
    return () => ac.abort();
  }, [classId]);

  useEffect(() => {
    if (!classId) {
      setClassPeriodsList([]);
      setClassPeriodsLoading(false);
      return;
    }
    const ac = new AbortController();
    const getClassPeriodsList = async () => {
      setClassPeriodsLoading(true);
      try {
        const result = await general_class_periods_for_member_api(classId, userInfo, ac.signal);
        if (isCanceledResponse(result)) return;
        if (result.code === 200) {
          const rows = (result.class_periods || []).filter((row) => row.status !== false);
          setClassPeriodsList(rows);
        } else {
          enqueueSnackbar(apiErrorText(result, t), { variant: "error" });
        }
      } finally {
        setClassPeriodsLoading(false);
      }
    };
    getClassPeriodsList();
    return () => ac.abort();
  }, [classId, userInfo, enqueueSnackbar, t]);

  return (
    <>
      <div className="container new-memories">
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="row input-form">
            {user_type !== "student" && (
              <div className="col-12 col-md-6 mt-4">
                <MUIAutocomplete
                  inputLabel={t("table.studentName")}
                  selectedOption={selectedStudent}
                  setSelectedOption={handleChangeStudent}
                  optionsList={studentsList}
                  formatOptionLabel={(option) => formatStudentListLabel(option)}
                  autoComplete="new-password"
                  name="name"
                  required
                />
              </div>
            )}
            <div className="col-12 col-md-6">
              <FormControl variant="outlined" className="mt-4 form-control">
                <InputLabel id="demo-simple-select-outlined-label">
                  {t("table.lessonBook")}
                </InputLabel>
                <Select
                  labelId="demo-simple-select-outlined-label"
                  id="demo-simple-select-outlined"
                  label={t("table.lessonBook")}
                  name="lesson_book"
                  className="mui-select"
                  value={state.lesson_book}
                  disabled
                >
                  <MenuItem value="quran">{t("lessonBook.quran")}</MenuItem>
                  <MenuItem value="quaida">{t("lessonBook.nooraniQuaida")}</MenuItem>
                  <MenuItem value="madani_quaida">{t("lessonBook.madaniQuaida")}</MenuItem>
                </Select>
              </FormControl>
            </div>
            {classId ? (
              <div className="col-12 col-md-6">
                <FormControl
                  variant="outlined"
                  className="mt-4 form-control"
                  required
                  disabled={classPeriodsLoading}
                >
                  <InputLabel id="hifz-class-period-label">{t("reportForm.classPeriod")}</InputLabel>
                  <Select
                    labelId="hifz-class-period-label"
                    id="hifz-class-period"
                    label={t("reportForm.classPeriod")}
                    name="class_period_id"
                    className="mui-select"
                    value={state.class_period_id || ""}
                    onChange={handleChange}
                  >
                    {classPeriodsList.map((period) => (
                      <MenuItem key={period._id} value={period._id}>
                        {`${period.title || t("class.defaultPeriodLabel")} (${period.start_time} - ${period.end_time})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            ) : null}
            {classId ? (
              <div className="col-12 col-md-6">
                <FormControl variant="outlined" className="mt-4 form-control" required>
                  <InputLabel id="hifz-session-type-label">{t("reportForm.sessionType")}</InputLabel>
                  <Select
                    labelId="hifz-session-type-label"
                    id="hifz-session-type"
                    label={t("reportForm.sessionType")}
                    name="session_type"
                    className="mui-select"
                    value={state.session_type || "lesson"}
                    onChange={(e) =>
                      setState((prev) => ({ ...prev, session_type: e.target.value }))
                    }
                  >
                    {HIFZ_SESSION_TYPE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {t(o.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            ) : null}
            {classId ? (
              <div className="col-12 col-md-6">
                <FormControl variant="outlined" className="mt-4 form-control" required>
                  <InputLabel id="hifz-lesson-rating-label">{t("reportForm.lessonRating")}</InputLabel>
                  <Select
                    labelId="hifz-lesson-rating-label"
                    id="hifz-lesson-rating"
                    label={t("reportForm.lessonRating")}
                    className="mui-select"
                    value={Number(state.lesson_rating) || 3}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        lesson_rating: Number(e.target.value),
                      }))
                    }
                  >
                    {HIFZ_LESSON_RATING_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {t(o.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            ) : null}
            {state.lesson_book === "quran" && (
              <div className="col-12 col-md-6">
                <TextField
                  className="mt-4 form-control"
                  type="number"
                  id="part_mumber"
                    label={t("reportForm.partNumber")}
                  variant="outlined"
                  name="part_mumber"
                  value={state.part_mumber}
                  required={true}
                  InputProps={{ inputProps: { min: 1, max: 30 } }}
                  onChange={handleChange}
                />
              </div>
            )}
            <div className="col-12 col-md-6">
              <TextField
                className="mt-4 form-control"
                type="number"
                id="page_number"
                label={t("reportForm.pageNumber")}
                variant="outlined"
                name="page_number"
                value={state.page_number}
                InputProps={{
                  inputProps: {
                    min:
                      state.lesson_book === "quran"
                        ? get_min_max_page_value(+state.part_mumber).min
                        : 1,
                    max:
                      state.lesson_book === "quran"
                        ? get_min_max_page_value(+state.part_mumber).max
                        : state.lesson_book === "madani_quaida"
                        ? 40
                        : 32,
                  },
                }}
                required
                onChange={handleChange}
              />
            </div>
            <div className="col-12 col-md-6">
              <TextField
                className="mt-4 form-control"
                type="number"
                id="line_number"
                label={t("reportForm.lineNumber")}
                variant="outlined"
                name="line_number"
                value={state.line_number}
                InputProps={{
                  inputProps: {
                    min: 1,
                    max:
                      state.lesson_book === "quran"
                        ? 16
                        : state.lesson_book === "madani_quaida"
                        ? 11
                        : 10,
                  },
                }}
                required={true}
                onChange={handleChange}
              />
            </div>
            {state.lesson_book === "quran" && (
              <div className="col-12 col-md-6">
                <TextField
                  className="mt-4 form-control"
                  type="number"
                  id="verse_number"
                  label={t("reportForm.verseNumber")}
                  variant="outlined"
                  name="verse_number"
                  value={state.verse_number}
                  onChange={handleChange}
                />
              </div>
            )}

            <div className="col-12">
              <TextField
                multiline
                rows={5}
                className="mt-4 form-control"
                id="report_description"
                label={classId ? t("reportForm.lessonDescription") : t("reportForm.reportDescription")}
                variant="outlined"
                name="report_description"
                value={state.report_description}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-12 add--button text-end mt-3">
            <button disabled={isLoading} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              {!isLoading && (formType === "ADD" ? <SaveIcon sx={{ fontSize: 18 }} /> : <EditIcon sx={{ fontSize: 18 }} />)}
              {isLoading
                ? formType === "ADD"
                  ? t("reportForm.saving")
                  : t("reportForm.updating")
                : formType === "ADD"
                ? t("common.save")
                : t("common.update")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
